/*
  Lista życzeń — serduszka na kartach i przy produkcie oraz licznik w nagłówku.

  Zastępuje skrypt zewnętrznej aplikacji. Nie trzyma żadnego stanu: pyta
  `window.wishlist` i przerysowuje się na zdarzeniu `wishlist:change`, więc
  serduszko dodane w karuzeli rekomendacji zapala się natychmiast także na
  karcie tego samego produktu w listingu obok.

  Obsługa jest delegowana z dokumentu, bo karty produktów dokładają się później
  (rekomendacje, doczytywanie listingu, szuflady) i nie da się ich podłączyć raz
  na starcie.
*/

(() => {
  const TRIGGER = "[data-wishlist-trigger]";
  const COUNTER = "[data-wishlist-count]";

  const addedClass = "wishlist-trigger--added";

  /*
    Wariant karty czytamy z odnośnika zdjęcia, a nie z atrybutu serduszka:
    listing pokazuje ten sam produkt raz na tkaninę i odnośnik jest jedynym
    miejscem, które zawsze niesie wariant tej konkretnej karty. Atrybut zostaje
    jako zapas (karta produktu, rekomendacje bez odnośnika z wariantem).
  */
  const variantFromCard = (element) => {
    const card = element.closest("product-card");
    const link = card && card.querySelector(".product-card__media");
    if (!link) return null;
    try {
      const href = (link.href || link.getAttribute("href") || "").replace(/&amp;/g, "&");
      return new URL(href, window.location.origin).searchParams.get("variant");
    } catch (error) {
      return null;
    }
  };

  const resolveVariant = (element) =>
    variantFromCard(element) || element.dataset.wishlistVariant;

  /*
    Tkanina jest drugą częścią tożsamości pozycji: listing pokazuje ten sam
    produkt raz na tkaninę, przy jednym wariancie. Bierzemy ją z atrybutu
    serduszka, a na karcie produktu z aktualnego adresu, gdzie tkaninę niesie
    parametr `fabric`.
  */
  const resolveFabric = (element) => {
    if (element.dataset.wishlistFabric) return element.dataset.wishlistFabric;
    const card = element.closest("product-card");
    if (card && card.dataset.initialOption) return card.dataset.initialOption;
    try {
      return new URLSearchParams(window.location.search).get("fabric") || "";
    } catch (error) {
      return "";
    }
  };

  const readTrigger = (element) => ({
    variantId: resolveVariant(element),
    productId: element.dataset.wishlistProduct,
    handle: element.dataset.wishlistHandle,
    fabric: resolveFabric(element),
  });

  const paintTrigger = (element) => {
    if (!window.wishlist) return;
    const inList = window.wishlist.has(resolveVariant(element), resolveFabric(element));
    element.classList.toggle(addedClass, inList);
    element.setAttribute("aria-pressed", String(inList));
    const label = inList
      ? element.dataset.wishlistLabelRemove
      : element.dataset.wishlistLabelAdd;
    if (label) {
      element.setAttribute("aria-label", label);
      element.setAttribute("title", label);
    }
  };

  const paintAll = () => {
    document.querySelectorAll(TRIGGER).forEach(paintTrigger);
    const count = window.wishlist ? window.wishlist.count() : 0;
    document.querySelectorAll(COUNTER).forEach((node) => {
      node.textContent = count > 0 ? String(count) : "";
      node.hidden = count === 0;
    });
  };

  /*
    Komunikat po dodaniu do listy jest osobną sekcją motywu (grupa szuflad).
    Otwieramy go tak, jak robi to reszta motywu — zdarzeniem `messageOpen`
    z identyfikatorem okna. Tylko przy dodaniu, nigdy przy usunięciu.
  */
  const openAddedMessage = () => {
    window.dispatchEvent(
      new CustomEvent("messageOpen", { detail: { id: "message-wishlist-added" } })
    );
  };

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(TRIGGER);
    if (!trigger || !window.wishlist) return;
    event.preventDefault();
    const item = readTrigger(trigger);
    if (!item.variantId) return;
    if (window.wishlist.has(item.variantId, item.fabric)) {
      window.wishlist.remove(item.variantId, item.fabric);
    } else if (window.wishlist.add(item)) {
      window.IWCAnalyticsSurfaces?.wishlistAdded(trigger);
      openAddedMessage();
    }
  });

  document.addEventListener("wishlist:change", paintAll);

  /*
    Na karcie produktu tkanina siedzi w adresie (parametr `fabric`), a jej
    zmiana nie przeładowuje strony - motyw podmienia adres przez historię
    przeglądarki. Bez nasłuchu serduszko zostawałoby zapalone po przejściu na
    kolor, którego nie ma na liście. Nakładamy się na obie metody historii
    i odmalowujemy stan.
  */
  const watchAddressChanges = () => {
    let last = window.location.search;
    const onAddressChange = () => {
      if (window.location.search === last) return;
      last = window.location.search;
      paintAll();
    };
    ["pushState", "replaceState"].forEach((name) => {
      const original = window.history[name];
      if (typeof original !== "function") return;
      window.history[name] = function patched(...args) {
        const result = original.apply(this, args);
        onAddressChange();
        return result;
      };
    });
    window.addEventListener("popstate", onAddressChange);
  };

  watchAddressChanges();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", paintAll);
  } else {
    paintAll();
  }

  /*
    Karty dokładane po wczytaniu strony (rekomendacje, doczytywanie listingu)
    muszą dostać właściwy stan serduszka bez czekania na zmianę listy.
  */
  new MutationObserver((records) => {
    const needsPaint = records.some((record) =>
      [...record.addedNodes].some(
        (node) =>
          node.nodeType === 1 && (node.matches?.(TRIGGER) || node.querySelector?.(TRIGGER))
      )
    );
    if (needsPaint) paintAll();
  }).observe(document.body, { childList: true, subtree: true });
})();
